import React from "react";
import { Settings } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import LegalLayout, { P, Strong, UL, ExtLink, InLink, DataTable } from "@/components/LegalLayout";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { Button } from "@/components/kit";
import { COMPANY } from "@/data/company";

const UPDATED = "1 juni 2026";

const CookiesPage = () => {
  const { reset: openCookieSettings } = useCookieConsent();

  useSeo({
    title: "Cookiebeleid | UPPIT",
    description:
      "Welke cookies gebruikt uppit.nl, waarvoor en hoe lang? Bekijk het volledige cookie-overzicht en beheer of trek je toestemming eenvoudig in.",
    path: "/cookies",
    ogSlug: "page/cookies",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Cookiebeleid UPPIT",
      url: "https://uppit.nl/cookies",
      inLanguage: "nl-NL",
      dateModified: "2026-06-01",
    },
  });

  const sections = [
    {
      id: "wat-zijn-cookies",
      title: "Wat zijn cookies?",
      content: (
        <>
          <P>
            Cookies zijn kleine tekstbestanden die bij je bezoek aan een website op je apparaat worden opgeslagen. In dit
            beleid gebruiken we "cookies" ook als verzamelnaam voor vergelijkbare technieken, zoals{" "}
            <Strong>localStorage</Strong> en <Strong>tracking pixels</Strong>.
          </P>
          <P>
            Cookies zorgen er bijvoorbeeld voor dat de website goed werkt, dat we (anoniem) begrijpen hoe bezoekers de site
            gebruiken, en dat advertenties op andere platforms relevanter worden.
          </P>
        </>
      ),
    },
    {
      id: "hoe-gebruiken",
      title: "Hoe gebruikt UPPIT cookies?",
      content: (
        <>
          <P>Wij verdelen cookies in drie categorieën:</P>
          <UL
            items={[
              <><Strong>Noodzakelijk</Strong> — vereist voor de basisfunctionaliteit van de website. Deze plaatsen we altijd.</>,
              <><Strong>Statistiek</Strong> — helpen ons anoniem te begrijpen hoe de site wordt gebruikt (Google Analytics 4).</>,
              <><Strong>Marketing</Strong> — voor relevantere advertenties op andere platforms (Meta Pixel en LinkedIn Insight Tag).</>,
            ]}
          />
          <P>
            <Strong>Statistiek- en marketingcookies plaatsen wij pas nadat jij daarvoor toestemming geeft</Strong> via de
            cookiebanner. Zonder toestemming worden deze scripts niet geladen.
          </P>
        </>
      ),
    },
    {
      id: "noodzakelijk",
      title: "Noodzakelijke cookies",
      content: (
        <>
          <P>Deze zijn nodig om de website te laten werken en jouw keuzes te onthouden. Hiervoor is geen toestemming vereist.</P>
          <DataTable
            testid="cookies-table-necessary"
            head={["Naam", "Aanbieder", "Doel", "Bewaartermijn"]}
            rows={[
              ["uppit_cookie_consent_v1", "UPPIT (localStorage)", "Onthoudt jouw cookievoorkeuren", "180 dagen"],
            ]}
          />
        </>
      ),
    },
    {
      id: "statistiek",
      title: "Statistiek — Google Analytics 4",
      content: (
        <>
          <P>
            Met jouw toestemming gebruiken wij <Strong>Google Analytics 4</Strong> om anoniem te meten hoe bezoekers de
            website gebruiken. Wij gebruiken deze inzichten uitsluitend om de website te verbeteren.
          </P>
          <DataTable
            testid="cookies-table-analytics"
            head={["Naam", "Aanbieder", "Doel", "Bewaartermijn"]}
            rows={[
              ["_ga", "Google Analytics", "Onderscheidt bezoekers (anonieme statistiek)", "2 jaar"],
              ["_ga_*", "Google Analytics", "Bewaart de sessiestatus per website", "2 jaar"],
            ]}
          />
        </>
      ),
    },
    {
      id: "marketing",
      title: "Marketing — Meta Pixel & LinkedIn Insight Tag",
      content: (
        <>
          <P>
            Met jouw toestemming gebruiken wij de <Strong>Meta Pixel</Strong> (Facebook/Instagram) en de{" "}
            <Strong>LinkedIn Insight Tag</Strong> om het effect van onze advertenties te meten en om relevantere
            advertenties te tonen aan mensen die interesse hebben getoond in onze diensten.
          </P>
          <DataTable
            testid="cookies-table-marketing"
            head={["Naam", "Aanbieder", "Doel", "Bewaartermijn"]}
            rows={[
              ["_fbp", "Meta", "Advertentiemeting en relevantere advertenties", "3 maanden"],
              ["_fbc", "Meta", "Herkomst van kliks op Meta-advertenties", "3 maanden"],
              ["bcookie", "LinkedIn", "Browseridentificatie", "1 jaar"],
              ["li_sugr", "LinkedIn", "Matchen van browseridentiteit", "90 dagen"],
              ["lidc", "LinkedIn", "Routering / datacenterselectie", "24 uur"],
            ]}
          />
        </>
      ),
    },
    {
      id: "beheren",
      title: "Toestemming beheren of intrekken",
      content: (
        <>
          <P>
            Je kunt je cookievoorkeuren op elk moment aanpassen of je toestemming intrekken — zonder opgaaf van reden.
            Klik op de knop hieronder om de cookie-instellingen direct te openen:
          </P>
          <Button variant="primary" testid="cookies-open-settings" onClick={openCookieSettings}>
            <Settings className="w-4 h-4" /> Cookie-instellingen aanpassen
          </Button>
          <P>
            Daarnaast kun je cookies altijd verwijderen of blokkeren via je browserinstellingen:{" "}
            <ExtLink href="https://support.google.com/chrome/answer/95647">Chrome</ExtLink>,{" "}
            <ExtLink href="https://support.mozilla.org/nl/kb/cookies-verwijderen-gegevens-wissen-websites-opgeslagen">Firefox</ExtLink>,{" "}
            <ExtLink href="https://support.apple.com/nl-nl/guide/safari/sfri11471/mac">Safari</ExtLink> of{" "}
            <ExtLink href="https://support.microsoft.com/nl-nl/microsoft-edge/cookies-verwijderen-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09">Edge</ExtLink>.
          </P>
        </>
      ),
    },
    {
      id: "derden",
      title: "Cookies van derden",
      content: (
        <>
          <P>
            Statistiek- en marketingcookies worden geplaatst door derde partijen. Hoe zij met jouw gegevens omgaan, lees je
            in hun eigen privacyverklaringen:
          </P>
          <UL
            items={[
              <><Strong>Google</Strong> — <ExtLink href="https://policies.google.com/privacy?hl=nl">policies.google.com/privacy</ExtLink></>,
              <><Strong>Meta</Strong> — <ExtLink href="https://www.facebook.com/privacy/policy/">facebook.com/privacy/policy</ExtLink></>,
              <><Strong>LinkedIn</Strong> — <ExtLink href="https://www.linkedin.com/legal/privacy-policy">linkedin.com/legal/privacy-policy</ExtLink></>,
            ]}
          />
        </>
      ),
    },
    {
      id: "contact",
      title: "Wijzigingen & contact",
      content: (
        <>
          <P>
            Wij kunnen dit cookiebeleid aanpassen, bijvoorbeeld wanneer we nieuwe tools inzetten. De actuele versie staat
            altijd op deze pagina. Hoe wij verder met persoonsgegevens omgaan, lees je in ons{" "}
            <InLink to="/privacy">privacybeleid</InLink>.
          </P>
          <P>
            Vragen? Mail naar <ExtLink href={`mailto:${COMPANY.email}`}>{COMPANY.email}</ExtLink> of stuur een
            WhatsApp-bericht naar {COMPANY.phoneDisplay}.
          </P>
        </>
      ),
    },
  ];

  return (
    <LegalLayout
      testid="cookies-page"
      crumbLabel="Cookiebeleid"
      crumbHref="/cookies"
      kicker="Juridisch · Cookies"
      italic="Cookiebeleid."
      bold="Jij bepaalt wat we mogen."
      lede="Welke cookies gebruikt uppit.nl, waarvoor en hoe lang? Hieronder vind je het volledige overzicht — en pas je jouw voorkeuren met één klik aan."
      updated={UPDATED}
      sections={sections}
    />
  );
};

export default CookiesPage;
